import type { GetTokenSilentlyOptions, LogoutOptions } from '@auth0/auth0-react'
import createClient from 'openapi-fetch'

import type { PathsWithMethod } from 'openapi-typescript-helpers'
import type { paths } from '~/api/BackendClient/generated-schema'
import CultureOperationsAPI from '~/api/backend/cultureOperationsApi'
import BulkExportCultureMetadataAPI from '~/api/backend/pages/bulkExportCultureMetadataAPI'
import DashboardPageAPI from '~/api/backend/pages/dashboardPageAPI'
import PlatePageAPI from '~/api/backend/pages/platePageAPI'
import WellPageAPI from '~/api/backend/pages/wellPageAPI'
import UserAPI from '~/api/backend/userAPI'
import CustomCultureStatusAPI from '../backend/customCultureStatusAPI'
import CulturesPageAPI from '../backend/pages/culturesPageAPI'
import RepresentativeImagesAPI from '../backend/representativeImagesAPI'
import { BackendHTTPError } from '../shared/BackendHTTPError'
import type { OpenApiFetchOptions, OpenApiFetchResponse } from '../shared/openApiTypes'
import { unwrapBackendResponse } from '../shared/unwrapBackendResponse'

class BackendClient {
  // Authentication
  getAccessTokenSilently: (options?: GetTokenSilentlyOptions) => Promise<string>
  logout: (options?: LogoutOptions) => Promise<void>
  user: UserAPI

  // Pages
  dashboardPage: DashboardPageAPI
  platePage: PlatePageAPI
  wellPage: WellPageAPI
  bulkExportCultureMetadata: BulkExportCultureMetadataAPI
  culturesPage: CulturesPageAPI

  // Other Resources
  customCultureStatuses: CustomCultureStatusAPI
  cultureOperations: CultureOperationsAPI
  representativeImages: RepresentativeImagesAPI

  constructor(getAccessTokenSilently, logout) {
    this.getAccessTokenSilently = getAccessTokenSilently
    this.logout = logout
    this.user = new UserAPI(this)
    this.dashboardPage = new DashboardPageAPI(this)
    this.platePage = new PlatePageAPI(this)
    this.wellPage = new WellPageAPI(this)
    this.bulkExportCultureMetadata = new BulkExportCultureMetadataAPI(this)
    this.customCultureStatuses = new CustomCultureStatusAPI(this)
    this.cultureOperations = new CultureOperationsAPI(this)
    this.culturesPage = new CulturesPageAPI(this)
    this.representativeImages = new RepresentativeImagesAPI(this)
  }

  _logoutDueToInvalidToken = async () => {
    // Do NOT invalidate tokens again here. This will cause an infinite loop.
    // The token is already invalid.
    await this.logout()
  }

  _fetchAccessToken = async () => {
    let token = ''
    try {
      token = await this.getAccessTokenSilently()
    } catch (e) {
      // The cached refresh token might be invalid if the OAuth parameters
      // have changed since it was created. For example, a new version of the
      // frontend might specify a different client ID or scope. Auth0 throws
      // an error that says the token is "missing," but in reality the token
      // is there, just no longer valid. The user needs to re-authenticate to
      // get a new refresh token.
      if ((e as Error).message?.includes('Missing Refresh Token')) {
        console.info('Logging out due to missing refresh token.')
        await this._logoutDueToInvalidToken()
      }
      return
    }
    return token
  }

  _createClient = async () => {
    const token = await this._fetchAccessToken()
    return createClient<paths>({
      baseUrl: process.env.MONOMER_BACKEND_V2_ORIGIN,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  }

  /**
   * Wraps API calls to handle 401 responses by triggering re-login.
   * This handles cases where the token is malformed, expired, or invalidated
   * server-side (e.g., via tokens_not_valid_before).
   */
  _withAuthErrorHandling = async <T>(apiCall: () => Promise<T>): Promise<T> => {
    try {
      return await apiCall()
    } catch (e) {
      if (e instanceof BackendHTTPError && e.statusCode === 401) {
        console.info('Received 401 from backend, logging out to trigger re-login.')
        await this._logoutDueToInvalidToken()
      }
      throw e
    }
  }

  async GET<P extends PathsWithMethod<paths, 'get'>>(
    url: P,
    ...init: OpenApiFetchOptions<paths, 'get', P>
  ): Promise<NonNullable<OpenApiFetchResponse<paths, 'get', P>['data']>> {
    return this._withAuthErrorHandling(async () => {
      const client = await this._createClient()
      const response = client.GET(url, ...init)
      return unwrapBackendResponse(response)
    })
  }

  async POST<P extends PathsWithMethod<paths, 'post'>>(
    url: P,
    ...init: OpenApiFetchOptions<paths, 'post', P>
  ): Promise<NonNullable<OpenApiFetchResponse<paths, 'post', P>['data']>> {
    return this._withAuthErrorHandling(async () => {
      const client = await this._createClient()
      const response = client.POST(url, ...init)
      return unwrapBackendResponse(response)
    })
  }

  async PUT<P extends PathsWithMethod<paths, 'put'>>(
    url: P,
    ...init: OpenApiFetchOptions<paths, 'put', P>
  ): Promise<NonNullable<OpenApiFetchResponse<paths, 'put', P>['data']>> {
    return this._withAuthErrorHandling(async () => {
      const client = await this._createClient()
      const response = client.PUT(url, ...init)
      return unwrapBackendResponse(response)
    })
  }

  async DELETE<P extends PathsWithMethod<paths, 'delete'>>(
    url: P,
    ...init: OpenApiFetchOptions<paths, 'delete', P>
  ): Promise<NonNullable<OpenApiFetchResponse<paths, 'delete', P>['data']>> {
    return this._withAuthErrorHandling(async () => {
      const client = await this._createClient()
      const response = client.DELETE(url, ...init)
      return unwrapBackendResponse(response)
    })
  }
}

export default BackendClient
